import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearSession, getSession } from '@/api/portal';
import { usePlatformBrand } from '@/contexts/PlatformBrandContext';

const navItems = [
  { label: '仪表盘', path: '/dashboard', icon: 'ri-dashboard-3-line' },
  { label: '交易记录', path: '/transactions', icon: 'ri-exchange-dollar-line' },
  { label: '通道列表', path: '/channels', icon: 'ri-route-line' },
  { label: '应用密钥', path: '/merchant-keys', icon: 'ri-key-2-line' },
  { label: 'API 文档', path: '/api-docs', icon: 'ri-code-box-line' },
  { label: '结算提现', path: '/withdraw', icon: 'ri-money-cny-circle-line' },
  { label: '提现账号', path: '/payment-accounts', icon: 'ri-bank-card-line' },
  { label: '商户配置', path: '/settings', icon: 'ri-settings-4-line' },
];

export default function Sidebar() {
  const { platformName } = usePlatformBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const session = getSession();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setCollapsed(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const logout = () => {
    clearSession();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <button
        className="fixed top-4 left-4 z-50 lg:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-slate-800/90 border border-slate-700/80 text-slate-300"
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-line'} />
      </button>

      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-slate-900/95 backdrop-blur-xl border-r border-slate-700/60 flex flex-col transition-all duration-300 ${collapsed ? 'w-16' : 'w-60'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className={`flex items-center gap-3 h-16 px-4 border-b border-slate-700/60 ${collapsed ? 'justify-center px-2' : ''}`}>
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-600 shrink-0">
            <i className="ri-bank-card-line text-white text-sm" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <span className="text-sm font-bold text-slate-100 whitespace-nowrap">{platformName}支付商户端</span>
              <p className="text-[10px] text-slate-500 whitespace-nowrap truncate">{session?.merchantNo || '商户门户'}</p>
            </div>
          )}
        </div>

        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                title={collapsed ? item.label : undefined}
                className={`relative w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${collapsed ? 'justify-center px-2 py-3' : 'px-3 py-2.5'} ${active ? 'bg-blue-500/10 text-blue-400' : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'}`}
              >
                <span className={`w-5 h-5 flex items-center justify-center shrink-0 ${active ? 'text-blue-400' : 'text-slate-500'}`}>
                  <i className={`${item.icon} text-base`} />
                </span>
                {!collapsed && <span className="truncate">{item.label}</span>}
                {active && <span className="absolute left-0 w-1 h-8 rounded-r-full bg-blue-500" />}
              </button>
            );
          })}
        </nav>

        <div className="mx-3 border-t border-slate-700/60" />
        <div className="py-3 px-2 space-y-1">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`hidden lg:flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-500 hover:bg-slate-800/80 hover:text-slate-300 ${collapsed ? 'justify-center px-2' : ''}`}
          >
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-base`} />
            {!collapsed && <span>收起菜单</span>}
          </button>
          <button
            onClick={logout}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-500 hover:bg-red-500/10 hover:text-red-300 ${collapsed ? 'justify-center px-2' : ''}`}
          >
            <i className="ri-logout-box-r-line text-base" />
            {!collapsed && <span>退出登录</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
